import { useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  CardHeader,
} from '@mui/material';
import Scrollbar from '../../../components/Scrollbar';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../../store/dashboardOrders/dashboardOrderSlice';
import Loader from '../../../components/Loader';
import RefreshIcon from '@mui/icons-material/Refresh';
import { findDashboardOrder } from '../../../store/dashboardOrders/api';
import { OrderStatus } from '../../../enum';
import { fDateTime } from '../../../utils/formatTime';
import Label from '../../../components/Label';
import { selectPaymentStatusColor } from '../../../utils/selectPaymentStatusColor';
import { encryptString } from '../../../utils/url';
import { Link } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const DashboardOrderTable = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.dashboardOrder);

  useEffect(() => {
    dispatch(
      findDashboardOrder({
        status: OrderStatus.pending,
        limit,
        skip,
      })
    );
  }, [dispatch, limit, skip]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleRefresh = () => {
    dispatch(
      findDashboardOrder({
        status: OrderStatus.pending,
        limit,
        skip,
      })
    );
  };

  return (
    <Card>
      <CardHeader
        title={'Orders'}
        action={
          <IconButton
            aria-label='settings'
            onClick={() => handleRefresh()}
          >
            <RefreshIcon />
          </IconButton>
        }
        sx={{ mb: 2 }}
      />

      <Scrollbar>
        <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
          <Table size={'medium'}>
            <TableHead>
              <TableRow>
                <TableCell align='center'>{'Order No.'}</TableCell>
                <TableCell align='center'>{'User'}</TableCell>
                <TableCell align='center'>{'Order Date'}</TableCell>
                <TableCell align='center'>{'Total Amount'}</TableCell>
                <TableCell align='center'>{'Identity'}</TableCell>
                <TableCell align='center'>{'Status'}</TableCell>
                <TableCell align='center'>{'Action'}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow hover>
                  <TableCell
                    colSpan={8}
                    align='center'
                  >
                    <Loader />
                  </TableCell>
                </TableRow>
              ) : data?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align='center'
                  >
                    {'No record found'}
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((order) => (
                  <TableRow key={order?.id}>
                    <TableCell align='center'>{order?.orderNumber}</TableCell>
                    <TableCell align='center'>
                      {order?.userDetails?.firstName}{' '}
                      {order?.userDetails?.lastName}
                    </TableCell>
                    <TableCell align='center'>
                      {fDateTime(order?.orderDate)}
                    </TableCell>
                    <TableCell align='center'>
                      {parseFloat(Number(order?.totalAmount ?? 0).toFixed(2))}
                    </TableCell>
                    <TableCell align='center'>
                      <Label
                        variant='ghost'
                        color={order?.isIdentitySecret ? 'success' : 'error'}
                      >
                        {order?.isIdentitySecret ? 'Secret' : 'Not Secret'}
                      </Label>
                    </TableCell>
                    <TableCell align='center'>
                      <Label
                        variant='ghost'
                        color={selectPaymentStatusColor(order?.status)}
                      >
                        {order?.status === 'shipped'
                          ? 'Out for Delivery'
                          : order?.status === 'confirmed'
                            ? 'Preparing'
                            : order?.status}
                      </Label>
                    </TableCell>
                    <TableCell align='center'>
                      <IconButton aria-label='visibility'>
                        <Link
                          to={`/order/view/${encryptString(order?.id ?? '')}`}
                          style={{ color: '#808080' }}
                        >
                          <RemoveRedEyeIcon />
                        </Link>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Scrollbar>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component='div'
          count={totalRecords}
          rowsPerPage={limit}
          page={Math.min(Math.round(totalRecords / limit), currentPage)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </Card>
  );
};

export default DashboardOrderTable;
